import { EColumnType, IColumnData } from '@/editor/data/model/model';
import { modelManager } from '@/editor/manager/entityManager';
import { TGlobalStores } from '@/editor/stores';
import { EToolType } from '@/editor/types';
import { clientPosToWorldPos, getUniqueId } from '@/editor/utils';
import { CommonController2d } from '../CommonController';
import { RulerModel } from '@/editor/entities/2d/rulerModel';
import { calculateBoxRulerInfos } from '@/editor/stores/utils/ruler/model';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { getAllStructOutlines } from '@/editor/stores/utils/column';
import { ColumnAbsorbManager } from '@/editor/manager/absorb/box/columnAbsorbManager';

export class DrawStruct extends CommonController2d {
  private _columnData: IColumnData | null = null;
  private _rulerModel = new RulerModel();
  private _structOutlines: { start: IPoint2d; end: IPoint2d }[] = [];
  private _columnAbsorbManager: ColumnAbsorbManager;

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._columnAbsorbManager = new ColumnAbsorbManager();
    this.updateData();
  }

  updateData() {
    this._structOutlines = getAllStructOutlines();
    this._columnAbsorbManager.updateData();
  }

  private setColumnData() {
    const { columnVirtualData } = this._gs.editorStateStore;
    if (!columnVirtualData) return;
    if (!(!this._columnData || (this._columnData && this._columnData.id !== columnVirtualData.id))) return;
    this._columnData = { ...columnVirtualData };
  }

  getColumnType() {
    let type = EColumnType.squareColumn;
    switch (this._gs.operateInfo.toolType) {
      case EToolType.squareColumn:
        type = EColumnType.squareColumn;
        break;
      case EToolType.cylinder:
        type = EColumnType.cylinder;
        break;
    }
    return type;
  }

  protected override mouseMove = (e: MouseEvent) => {
    this.setColumnData();
    if (!this._columnData) return;

    // 这一步不可少
    if (this._gs.editorStateStore.columnVirtualData) {
      this._columnData.rotation = this._gs.editorStateStore.columnVirtualData.rotation;
    }

    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPositionV2 = clientPosToWorldPos(e, camera, renderer);

    const width = this._columnData.type === EColumnType.cylinder ? this._columnData.radius * 2 : this._columnData.width;
    const thickness =
      this._columnData.type === EColumnType.cylinder ? this._columnData.radius * 2 : this._columnData.thickness;
    const absorbResult = this._columnAbsorbManager.calculateThenRender({
      center: worldPositionV2,
      width,
      thickness,
      rotation: this._columnData.rotation
    });

    if (absorbResult) {
      this._columnData.center = absorbResult.absorbPoint;
      this._columnData.rotation = absorbResult.rotation;
    } else {
      this._columnData.center = worldPositionV2;
    }

    modelManager.structManager.updateVirtualColumn(this._columnData);

    this.updateRuler();
  };

  protected override mouseUp = (e: MouseEvent) => {
    if (this._columnData) {
      if (e.button === 0) {
        const newColumnData = { ...this._columnData, id: getUniqueId() };
        this._gs.structOperation.addColumn(newColumnData, this._gs.editorStateStore.columnFaceMaterialMap || {});

        const { columnVirtualData } = this._gs.editorStateStore;
        if (columnVirtualData) {
          this._columnData.rotation = columnVirtualData.rotation;
        }

        this.updateData();
      } else if (e.button === 2) {
        this.dispose();
        this.exit();
      }
    }
  };

  private updateRuler() {
    if (!this._columnData) {
      this._rulerModel.clear();
      return;
    }

    const width = this._columnData.type === EColumnType.cylinder ? this._columnData.radius * 2 : this._columnData.width;
    const thickness =
      this._columnData.type === EColumnType.cylinder ? this._columnData.radius * 2 : this._columnData.thickness;
    const rulerInfos = calculateBoxRulerInfos(
      {
        center: this._columnData.center,
        width,
        thickness,
        rotation: this._columnData.rotation
      },
      this._structOutlines,
      true
    );
    this._rulerModel.updateModel(rulerInfos);
  }

  clear() {
    modelManager.structManager.disposeVirtualColumn();
    this._columnData = null;
    this._rulerModel.clear();
    this._columnAbsorbManager.clearModel();
    this._gs.editorStateStore.resetColumnVirtualData();
  }

  override dispose() {
    this.clear();
    this._rulerModel.dispose();
    this._columnAbsorbManager.dispose();
    super.dispose();
  }
}
